import { useState } from 'react';
import { isLiveState } from '@avslack/shared';
import { useHuddle } from '../huddle/HuddleProvider';
import { useRealtime } from '../realtime/RealtimeProvider';
import { usePhoneContacts } from '../data/phone';
import {
  COLLAPSED_HEIGHT,
  getPipWindow,
  resizeServicePip,
  setServicePipCollapsed,
  usePipCollapsed,
} from '../lib/service-pip';
import { CustomerCard } from './CustomerCard';
import { HuddleTimer } from './HuddleTimer';
import { Icon } from './Icon';
import { IncomingCall } from './IncomingCall';
import { PhoneLegs } from './PhoneLegs';
import { ServiceDial } from './ServiceDial';
import { TtCallNote } from './TtCallNote';

/**
 * A VEVOSZOLGALATI LEBEGO PANEL tartalma (docs/20-vevoszolgalat.html).
 *
 * Harom allapot: SZABAD (var), CSORGO (felveszem/elutasitom), HIVASBAN (hivasfunkciok).
 * A lebego ablakban rajzolodik (React-portal), ezert csak olyan elemeket hasznalhat,
 * amik nem a fo dokumentumra kotnek (Radix felugrok, `document` figyelok) - az
 * ide betett komponensek (PhoneLegs, CustomerCard, TtCallNote) ilyenek.
 *
 * OSSZECSUKVA mindharom allapot egyetlen sor: a panel nem takarja a tavoli asztalt,
 * de a csorges felveheto, hivasban a nemitas es a kilepes kezre esik.
 */

/** A panel (es a lebego ablak) magassaganak igazitasa a tartalomhoz - kattintasbol hivando. */
function fitToContent(): void {
  requestAnimationFrame(() => {
    const win = getPipWindow();
    const root = win?.document.getElementById('service-root');
    if (!win || !root) return;
    const chrome = win.outerHeight - win.innerHeight;
    resizeServicePip(Math.min(root.scrollHeight + chrome, 720));
  });
}

export function ServicePanel({
  meId,
  ringChannelId,
  onAccept,
  onDecline,
  onRingDone,
  onFullView,
  onFocusMain,
}: {
  meId: string;
  /** A csorgo hivas csatornaja, ha van. */
  ringChannelId: string | null;
  onAccept: (channelId: string) => void;
  onDecline: (channelId: string) => void;
  onRingDone: () => void;
  /** Teljes hivasfelulet a fo ablakban. */
  onFullView: () => void;
  onFocusMain: () => void;
}) {
  const { state } = useHuddle();
  const inCall = state !== 'idle' && state !== 'failed';
  const collapsed = usePipCollapsed();

  // A csorgo hivas az elso: ilyenkor meg nem vagyunk hivasban.
  if (ringChannelId && !isLiveState(state)) {
    if (collapsed) {
      return (
        <CollapsedRow>
          <div className="min-w-0 flex-1">
            <IncomingCall
              variant="bar"
              channelId={ringChannelId}
              meId={meId}
              onAccept={() => onAccept(ringChannelId)}
              onDecline={() => onDecline(ringChannelId)}
              onDone={onRingDone}
            />
          </div>
        </CollapsedRow>
      );
    }
    return (
      <IncomingCall
        variant="panel"
        channelId={ringChannelId}
        meId={meId}
        onAccept={() => onAccept(ringChannelId)}
        onDecline={() => onDecline(ringChannelId)}
        onDone={onRingDone}
      />
    );
  }

  if (inCall) {
    return collapsed ? <CallRow /> : <CallView meId={meId} onFullView={onFullView} />;
  }
  return collapsed ? <IdleRow /> : <IdleView meId={meId} onFocusMain={onFocusMain} />;
}

// ─── Osszecsukas ──────────────────────────────────────────────────────────────

/** Osszecsuko / lenyito gomb - minden nezet fejlec-soranak vegen. */
function CollapseToggle() {
  const collapsed = usePipCollapsed();
  const label = collapsed ? 'Panel lenyitása' : 'Panel összecsukása';
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-expanded={!collapsed}
      onClick={() => setServicePipCollapsed(!collapsed)}
      className="grid size-7 shrink-0 place-items-center rounded-control text-ink-muted hover:bg-surface-hover hover:text-ink"
    >
      <Icon
        name="le"
        className={`transition-transform duration-(--duration-quick) ${collapsed ? '' : 'rotate-180'}`}
      />
    </button>
  );
}

/** Az osszecsukott panel egyetlen sora (pontosan `COLLAPSED_HEIGHT` magas). */
function CollapsedRow({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ height: COLLAPSED_HEIGHT }} className="flex items-center gap-2 bg-surface px-2.5">
      {children}
      <CollapseToggle />
    </div>
  );
}

function IdleRow() {
  const { status } = useRealtime();
  const offline = status !== 'online';
  return (
    <CollapsedRow>
      <span
        aria-hidden
        className={`size-2.5 shrink-0 rounded-full ${
          offline ? 'bg-[var(--danger)]' : 'bg-[var(--presence-active)]'
        }`}
      />
      <p className="min-w-0 flex-1 truncate font-display text-sm font-semibold text-ink">
        {offline ? 'Nincs kapcsolat' : 'Vevőszolgálat · szabad'}
      </p>
    </CollapsedRow>
  );
}

function CallRow() {
  const { huddle, state, isMuted, toggleMute, leave } = useHuddle();
  const phoneContacts = usePhoneContacts();
  const number = huddle?.phoneLegs.find((leg) => leg.callerNumber)?.callerNumber ?? null;
  const title = !huddle
    ? 'Csatlakozás…'
    : number
      ? phoneContacts.display(number)
      : (huddle.title ?? 'Ölelés');

  return (
    <CollapsedRow>
      <span
        aria-hidden
        className={`size-2.5 shrink-0 rounded-full ${isLiveState(state) ? 'bg-[var(--danger)]' : 'bg-ink-faint'}`}
      />
      <p className="min-w-0 flex-1 truncate font-display text-sm font-semibold">{title}</p>
      {huddle && (
        <HuddleTimer startedAt={huddle.startedAt} className="text-xs font-medium text-ink-muted" />
      )}
      <button
        type="button"
        onClick={toggleMute}
        title={isMuted ? 'Némítás feloldása' : 'Némítás'}
        aria-label={isMuted ? 'Némítás feloldása' : 'Némítás'}
        aria-pressed={isMuted}
        className={`grid size-7 shrink-0 place-items-center rounded-control border ${
          isMuted
            ? 'border-border-soft text-ink-muted hover:bg-surface-hover'
            : 'border-accent bg-accent-soft text-accent'
        }`}
      >
        <Icon name={isMuted ? 'mikrofonNema' : 'mikrofon'} />
      </button>
      <button
        type="button"
        onClick={leave}
        title="Kilépés a hívásból"
        aria-label="Kilépés a hívásból"
        className="grid size-7 shrink-0 place-items-center rounded-control bg-[var(--danger)] text-white"
      >
        <Icon name="kilepes" />
      </button>
    </CollapsedRow>
  );
}

// ─── Szabad ───────────────────────────────────────────────────────────────────

function IdleView({ meId, onFocusMain }: { meId: string; onFocusMain: () => void }) {
  const { status } = useRealtime();
  const offline = status !== 'online';

  return (
    <div className="flex min-h-full flex-col gap-2.5 bg-surface p-3">
      <div className="flex items-center gap-2">
        <span
          aria-hidden
          className={`size-2.5 shrink-0 rounded-full ${
            offline ? 'bg-[var(--danger)]' : 'bg-[var(--presence-active)]'
          }`}
        />
        <p className="min-w-0 flex-1 truncate font-display text-sm font-semibold text-ink">
          {offline ? 'Nincs kapcsolat' : 'Vevőszolgálat · szabad'}
        </p>
        <button
          type="button"
          onClick={onFocusMain}
          className="shrink-0 rounded-control border border-border-soft px-2 py-0.5 text-2xs text-ink-muted hover:bg-surface-hover hover:text-ink"
        >
          AVSlack megnyitása
        </button>
        <CollapseToggle />
      </div>
      {offline && (
        <p className="text-xs text-ink-muted" role="status">
          A kapcsolat megszakadt, ezért most nem érkezik hívás. Újracsatlakozunk.
        </p>
      )}

      {/* Hívás indítása: kolléga (ölelés), ügyfél vagy telefonszám. */}
      <ServiceDial meId={meId} />
    </div>
  );
}

// ─── Hivasban ─────────────────────────────────────────────────────────────────

function CallView({ meId, onFullView }: { meId: string; onFullView: () => void }) {
  const { huddle, state, isMuted, toggleMute, leave, speakingIds, error, dismissError } =
    useHuddle();
  const phoneContacts = usePhoneContacts();
  const [cardOpen, setCardOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);

  if (!huddle) {
    return (
      <div className="grid h-full place-items-center bg-surface p-4 text-sm text-ink-muted">
        {state === 'failed' ? 'A hívás nem indult el.' : 'Csatlakozás…'}
      </div>
    );
  }

  const number = huddle.phoneLegs.find((leg) => leg.callerNumber)?.callerNumber ?? null;
  const title = number ? phoneContacts.display(number) : (huddle.title ?? 'Ölelés');
  const live = isLiveState(state);

  return (
    <div className="flex min-h-full flex-col gap-2.5 bg-surface p-3">
      <div className="flex items-center gap-2">
        <span
          aria-hidden
          className={`size-2.5 shrink-0 rounded-full ${live ? 'bg-[var(--danger)]' : 'bg-ink-faint'}`}
        />
        <p className="min-w-0 flex-1 truncate font-display text-sm font-semibold">{title}</p>
        <HuddleTimer startedAt={huddle.startedAt} className="text-xs font-medium text-ink-muted" />
        <CollapseToggle />
      </div>

      {error && (
        <div
          role="alert"
          className="flex items-center gap-2 rounded-control bg-[var(--danger-soft)] px-2.5 py-1.5 text-xs"
        >
          <span className="min-w-0 flex-1">{error}</span>
          <button type="button" onClick={dismissError} className="font-semibold">
            Értem
          </button>
        </div>
      )}

      {/* A telefonos fel: allapot, ATADAS, telefon bontasa (a meglevo komponens). */}
      <PhoneLegs huddle={huddle} meId={meId} speakingIds={speakingIds} />

      <div className="flex flex-wrap gap-1.5">
        <PanelButton
          label={isMuted ? 'Némítás feloldása' : 'Némítás'}
          active={!isMuted}
          onClick={toggleMute}
        >
          <Icon name={isMuted ? 'mikrofonNema' : 'mikrofon'} />
          {isMuted ? 'Némítva' : 'Mikrofon'}
        </PanelButton>

        {number && (
          <>
            <PanelButton
              label="Ügyfélkártya"
              active={cardOpen}
              onClick={() => {
                setCardOpen((open) => !open);
                fitToContent();
              }}
            >
              <Icon name="ugyfel" /> Ügyfél
            </PanelButton>
            <PanelButton
              label="Hívásjegyzet"
              active={noteOpen}
              onClick={() => {
                setNoteOpen((open) => !open);
                fitToContent();
              }}
            >
              <Icon name="jegyzet" /> Jegyzet
            </PanelButton>
          </>
        )}

        <PanelButton label="Teljes hívásfelület a fő ablakban" onClick={onFullView}>
          <Icon name="megnyitas" /> Teljes nézet
        </PanelButton>

        <button
          type="button"
          onClick={leave}
          title="Kilépés a hívásból"
          className="ml-auto flex items-center gap-1.5 rounded-control bg-[var(--danger)] px-3 py-1.5 text-xs font-semibold text-white transition-transform active:scale-95"
        >
          <Icon name="kilepes" /> Kilépés
        </button>
      </div>

      {number && cardOpen && (
        <div className="rounded-control border border-border-soft bg-surface-raised p-2">
          <CustomerCard number={number} assignable />
        </div>
      )}
      {number && noteOpen && (
        <div className="rounded-control border border-border-soft bg-surface-raised p-2">
          <TtCallNote huddleId={huddle.id} number={number} live={live} embedded />
        </div>
      )}
    </div>
  );
}

function PanelButton({
  label,
  active = false,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      className={`flex items-center gap-1.5 rounded-control border px-2.5 py-1.5 text-xs font-medium transition-colors ${
        active
          ? 'border-accent bg-accent-soft text-accent'
          : 'border-border-soft text-ink-muted hover:bg-surface-hover hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}
